// First-run value onboarding (PRD §4): shown on the user's first AI click.
// Three slides — Skizze → KI → fertiges Werk — ending in a "3 gratis testen"
// CTA that proceeds with the AI action. Shown at most once (persisted seen
// flag). Accessible: role=dialog, focus trap, ESC, arrow-key slide nav,
// prefers-reduced-motion. Pure DOM, dependency-injected, jsdom-tested.

import { trapBackground } from './overlay-a11y';

export interface OnboardingStorage {
  get(key: string): string | null;
  set(key: string, value: string): void;
}

export interface OnboardingDeps {
  /** Called when the user taps "3 gratis testen" (proceed with the AI action). */
  onComplete: () => unknown;
  /** Called when the user dismisses without completing. */
  onDismiss?: () => unknown;
  /** Persisted seen-flag store (default: localStorage, jsdom-safe). */
  storage?: OnboardingStorage;
  doc?: Document;
  prefersReducedMotion?: () => boolean;
}

export interface OnboardingHandle {
  /** Show only if not seen before. Returns true if it actually opened. */
  maybeShow: () => boolean;
  /** Force-show regardless of the seen flag. */
  show: () => void;
  close: () => void;
  readonly isOpen: boolean;
}

const SEEN_KEY = 'joeflow_onboarding_seen_v1';
const STYLE_ID = 'jf-onboarding-style';

interface Slide {
  emoji: string;
  title: string;
  body: string;
}

const SLIDES: readonly Slide[] = [
  {
    emoji: '✏️',
    title: 'Zeichne deine Skizze',
    body: 'Mit allen Werkzeugen — komplett kostenlos, ohne Anmeldung.',
  },
  {
    emoji: '✨',
    title: 'KI verwandelt sie',
    body: 'Ein Tipp und JOE FLOW AI macht aus deiner Skizze ein fertiges Werk.',
  },
  {
    emoji: '🎨',
    title: 'Fertig zum Teilen',
    body: 'Exportiere dein Werk und teile es. Die ersten 3 KI-Zauber gehen aufs Haus.',
  },
];

const STYLES = `
.jf-ob-overlay{position:fixed;inset:0;z-index:2100;display:flex;align-items:center;
  justify-content:center;padding:16px;background:rgba(15,23,42,.78);backdrop-filter:blur(4px);}
.jf-ob-dialog{background:#1e293b;color:#f1f5f9;width:min(460px,100%);border-radius:16px;
  padding:24px;box-shadow:0 20px 60px rgba(0,0,0,.5);font-family:system-ui,-apple-system,sans-serif;
  text-align:center;}
.jf-ob-anim{animation:jf-ob-in .18s ease-out;}
@keyframes jf-ob-in{from{opacity:0;transform:scale(.97);}to{opacity:1;transform:none;}}
.jf-ob-emoji{font-size:3rem;line-height:1;margin-bottom:8px;}
.jf-ob-title{font-size:1.3rem;font-weight:700;margin:0 0 6px;}
.jf-ob-body{color:#cbd5e1;font-size:.95rem;margin:0 0 16px;}
.jf-ob-dots{display:flex;gap:6px;justify-content:center;margin-bottom:16px;}
.jf-ob-dot{width:8px;height:8px;border-radius:50%;background:#334155;border:0;padding:0;cursor:pointer;}
.jf-ob-dot[aria-current="step"]{background:#22d3ee;width:20px;border-radius:4px;}
.jf-ob-actions{display:flex;gap:8px;}
.jf-ob-btn{flex:1;min-height:44px;border-radius:10px;border:1px solid #334155;background:#0f172a;
  color:#cbd5e1;font-size:.95rem;cursor:pointer;}
.jf-ob-btn-primary{border:0;background:#e67e22;color:#fff;font-weight:700;}
.jf-ob-btn-primary:hover,.jf-ob-btn-primary:focus-visible{background:#d35400;}
.jf-ob-skip{margin-top:10px;background:transparent;border:0;color:#94a3b8;cursor:pointer;
  min-height:44px;font-size:.85rem;text-decoration:underline;}
@media(prefers-reduced-motion:reduce){.jf-ob-anim{animation:none;}}
`;

const FOCUSABLE = 'button:not([disabled]),[tabindex]:not([tabindex="-1"])';

function defaultReducedMotion(): boolean {
  try {
    return (
      typeof globalThis.matchMedia === 'function' &&
      globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  } catch {
    return false;
  }
}

function localStore(): OnboardingStorage {
  return {
    get: (k) => {
      try {
        return globalThis.localStorage?.getItem(k) ?? null;
      } catch {
        return null;
      }
    },
    set: (k, v) => {
      try {
        globalThis.localStorage?.setItem(k, v);
      } catch {
        /* private mode / quota — onboarding may reappear, acceptable */
      }
    },
  };
}

export function mountOnboarding(deps: OnboardingDeps): OnboardingHandle {
  const doc = deps.doc ?? document;
  const store = deps.storage ?? localStore();
  const reducedMotion = deps.prefersReducedMotion ?? defaultReducedMotion;

  let overlay: HTMLElement | null = null;
  let lastFocused: Element | null = null;
  let index = 0;
  let open = false;
  let releaseBackground: (() => void) | null = null;

  function ensureStyle(): void {
    if (doc.getElementById(STYLE_ID)) return;
    const style = doc.createElement('style');
    style.id = STYLE_ID;
    style.textContent = STYLES;
    (doc.head ?? doc.body ?? doc.documentElement).appendChild(style);
  }

  function markSeen(): void {
    store.set(SEEN_KEY, '1');
  }

  function render(): HTMLElement {
    const slide = SLIDES[index]!;
    const isLast = index === SLIDES.length - 1;
    const titleId = 'jf-ob-title';
    const descId = 'jf-ob-desc';

    const ov = doc.createElement('div');
    ov.className = 'jf-ob-overlay';

    const dialog = doc.createElement('div');
    dialog.className = 'jf-ob-dialog' + (reducedMotion() ? '' : ' jf-ob-anim');
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-labelledby', titleId);
    dialog.setAttribute('aria-describedby', descId);

    const dots = SLIDES.map(
      (_, i) =>
        `<button type="button" class="jf-ob-dot" data-goto="${i}"${i === index ? ' aria-current="step"' : ''} aria-label="Slide ${i + 1}"></button>`,
    ).join('');

    dialog.innerHTML = `
      <div class="jf-ob-emoji" aria-hidden="true">${slide.emoji}</div>
      <h2 class="jf-ob-title" id="${titleId}">${slide.title}</h2>
      <p class="jf-ob-body" id="${descId}">${slide.body}</p>
      <div class="jf-ob-dots" role="group" aria-label="Fortschritt">${dots}</div>
      <div class="jf-ob-actions">
        ${index > 0 ? '<button type="button" class="jf-ob-btn" data-act="back">Zurück</button>' : ''}
        <button type="button" class="jf-ob-btn jf-ob-btn-primary" data-act="${isLast ? 'complete' : 'next'}">
          ${isLast ? '3 gratis testen' : 'Weiter'}
        </button>
      </div>
      <button type="button" class="jf-ob-skip" data-act="skip">Überspringen</button>`;

    ov.appendChild(dialog);
    return ov;
  }

  function rerender(): void {
    if (!overlay) return;
    const fresh = render();
    overlay.replaceWith(fresh);
    overlay.removeEventListener('click', onClick);
    overlay = fresh;
    overlay.addEventListener('click', onClick);
    overlay.querySelector<HTMLElement>('.jf-ob-btn-primary')?.focus();
  }

  function complete(): void {
    markSeen();
    close();
    deps.onComplete();
  }

  function skip(): void {
    markSeen();
    close();
    deps.onDismiss?.();
  }

  function onClick(e: Event): void {
    const t = e.target as HTMLElement;
    const act = t.closest<HTMLElement>('[data-act]')?.dataset.act;
    if (act === 'next') {
      index = Math.min(index + 1, SLIDES.length - 1);
      rerender();
    } else if (act === 'back') {
      index = Math.max(index - 1, 0);
      rerender();
    } else if (act === 'complete') {
      complete();
    } else if (act === 'skip') {
      skip();
    }
    const goto = t.closest<HTMLElement>('[data-goto]')?.dataset.goto;
    if (goto !== undefined) {
      index = Math.max(0, Math.min(Number(goto), SLIDES.length - 1));
      rerender();
    }
  }

  function onKeydown(e: KeyboardEvent): void {
    if (!overlay) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      skip();
      return;
    }
    if (e.key === 'ArrowRight') {
      index = Math.min(index + 1, SLIDES.length - 1);
      rerender();
      return;
    }
    if (e.key === 'ArrowLeft') {
      index = Math.max(index - 1, 0);
      rerender();
      return;
    }
    if (e.key !== 'Tab') return;
    const focusables = Array.from(overlay.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (focusables.length === 0) return;
    const first = focusables[0]!;
    const last = focusables[focusables.length - 1]!;
    if (e.shiftKey && doc.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && doc.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function show(): void {
    if (overlay) return;
    ensureStyle();
    index = 0;
    lastFocused = doc.activeElement;
    overlay = render();
    (doc.body ?? doc.documentElement).appendChild(overlay);
    releaseBackground = trapBackground(overlay);
    overlay.addEventListener('click', onClick);
    doc.addEventListener('keydown', onKeydown, true);
    open = true;
    overlay.querySelector<HTMLElement>('.jf-ob-btn-primary')?.focus();
  }

  function close(): void {
    if (!overlay) return;
    overlay.removeEventListener('click', onClick);
    overlay.remove();
    overlay = null;
    open = false;
    releaseBackground?.();
    releaseBackground = null;
    doc.removeEventListener('keydown', onKeydown, true);
    if (lastFocused instanceof HTMLElement) lastFocused.focus();
  }

  function maybeShow(): boolean {
    if (store.get(SEEN_KEY) === '1') return false;
    show();
    return true;
  }

  return {
    maybeShow,
    show,
    close,
    get isOpen() {
      return open;
    },
  };
}
